// E-mail em massa a partir da lista de clientes (specs/15-email.md §7.1).
import { montarContexto, renderizar, renderizarEmail } from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal } from '../components/ui';
import { hojeISO, num } from '../format';
import { useTemplates } from './Templates';
import { corpoEmailFinal, corpoEmailVazio, EditorEmail, paraHtmlEmail, PreviaEmailHtml } from './EditorEmail';

export type AlvoMassa = { clienteIds: string[] } | { filtros: Record<string, string> };
interface Previa {
  selecionados: number; elegiveis: number; naoContactar: number; semEmail: number; descadastrados: number; semVendedor: number;
  remetente: 'EU' | 'VENDEDOR_DO_CLIENTE';
  porRemetente: Array<{ id: string; nome: string; qtd: number; caixaOk: boolean }>;
  amostra: Array<{ id: string; nome: string; empresa: string | null; cidade: string | null; email: string; ultimaCompra: string | null; valorUltimaCompra: string | null; remetenteNome: string }>;
  limiteHora: number | null; limiteDia: number | null; maximo: number;
}

export function ModalEmailMassa({ alvo, onClose }: { alvo: AlvoMassa; onClose: () => void }) {
  const { me, pode } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const gestor = pode('CAMPANHAS_GERENCIAR');
  const [remetente, setRemetente] = useState<'EU' | 'VENDEDOR_DO_CLIENTE'>('EU');
  const templates = useTemplates('EMAIL');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [assunto, setAssunto] = useState('');
  const [corpo, setCorpo] = useState('<p>Olá, {{primeiro_nome}}!</p><p><br></p>');
  const [verPrevia, setVerPrevia] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const prev = useQuery({
    queryKey: ['email-massa-previa', JSON.stringify(alvo), remetente],
    queryFn: () => api<Previa>('/emails/massa/previa', { body: { ...alvo, remetente } }),
  });
  const p = prev.data;
  const amostra = p?.amostra[0];
  const ctx = montarContexto({
    cliente: amostra ?? { nome: 'Cliente Exemplo', empresa: 'Empresa Exemplo', cidade: null },
    vendedorNome: amostra?.remetenteNome ?? me?.usuario.nome, empresaVendedora: me?.empresa.nome, hoje: hojeISO(),
  });
  const pa = renderizar(assunto, ctx);
  const corpoLimpo = corpoEmailFinal(corpo);
  const pc = renderizarEmail(corpoLimpo, ctx);
  const semCaixa = p?.porRemetente.filter((r) => !r.caixaOk) ?? [];

  const usarTemplate = (id: string) => {
    const t = templates.data?.find((x) => x.id === id);
    if (!t) return;
    setTemplateId(t.id);
    setAssunto(t.assunto ?? '');
    setCorpo(paraHtmlEmail(t.corpo));
  };
  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      const r = await api<{ campanhaId: string; total: number }>('/emails/massa', {
        body: { ...alvo, remetente, assunto, corpo: corpoLimpo, ...(templateId ? { templateId } : {}) },
      });
      await qc.invalidateQueries({ queryKey: ['campanhas'] });
      nav(`/campanhas/${r.campanhaId}`);
    } catch (e) {
      setErro(mensagemErro(e));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="E-mail em massa" onClose={onClose} largo>
      <h2 style={{ fontSize: 18 }}>E-mail em massa</h2>
      {prev.isLoading ? <Carregando /> : prev.error ? <Erro erro={mensagemErro(prev.error)} /> : p && (
        <div className="col" style={{ gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span className="num" style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>{num(p.elegiveis)}</span>
            <span className="muted">de {num(p.selecionados)} {p.selecionados === 1 ? 'cliente vai' : 'clientes vão'} receber</span>
          </div>
          <span className="small muted">
            {[p.semEmail && `${num(p.semEmail)} sem e-mail`, p.descadastrados && `${num(p.descadastrados)} descadastrados`,
              p.naoContactar && `${num(p.naoContactar)} não contactar`, p.semVendedor && `${num(p.semVendedor)} sem vendedor ativo`].filter(Boolean).join(' · ') || 'Todos podem receber.'}
          </span>
          {p.elegiveis > p.maximo && <div className="alert warn"><Icon name="alerta" /><span>No máximo {num(p.maximo)} clientes por envio. Filtre mais a lista.</span></div>}
        </div>
      )}

      {gestor && (
        <div className="field"><span>Enviar pela caixa de</span>
          <div className="seg" role="group" aria-label="Remetente">
            <button type="button" aria-pressed={remetente === 'EU'} onClick={() => setRemetente('EU')}>Minha caixa</button>
            <button type="button" aria-pressed={remetente === 'VENDEDOR_DO_CLIENTE'} onClick={() => setRemetente('VENDEDOR_DO_CLIENTE')}>Vendedor de cada cliente</button>
          </div>
          <span className="hint">{remetente === 'EU' ? 'Todos recebem do seu endereço e respondem para você.' : 'Cada cliente recebe do endereço do vendedor dele e responde para ele.'}</span>
        </div>
      )}
      {semCaixa.length > 0 && (
        <div className="alert warn"><Icon name="alerta" /><span>
          Sem caixa de e-mail conectada: {semCaixa.map((r) => `${r.nome} (${num(r.qtd)})`).join(', ')}. Esses e-mails ficam na fila até conectar em <Link to="/configuracoes/email">Configurações › E-mail de saída</Link>.
        </span></div>
      )}

      {!!templates.data?.length && (
        <label className="field"><span>Template</span>
          <select className="select" value={templateId ?? ''} onChange={(e) => (e.target.value ? usarTemplate(e.target.value) : setTemplateId(null))}>
            <option value="">Escrever do zero</option>
            {templates.data.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </label>
      )}
      <label className="field"><span>Assunto *</span><input className="input" maxLength={200} value={assunto} onChange={(e) => setAssunto(e.target.value)} /></label>
      <div className="field"><span>Mensagem *</span><EditorEmail valor={corpo} onChange={setCorpo} rotulo="Mensagem" /></div>
      {(assunto || !corpoEmailVazio(corpoLimpo)) && (
        <div className="col" style={{ gap: 8 }}>
          <button type="button" className="link-btn small" style={{ alignSelf: 'flex-start' }} aria-expanded={verPrevia} onClick={() => setVerPrevia(!verPrevia)}>
            {verPrevia ? 'Esconder' : 'Ver'} como {amostra ? amostra.nome : 'o cliente'} vai receber
          </button>
          {verPrevia && (
            <PreviaEmailHtml html={corpoLimpo} ctx={ctx} assunto={pa.texto}>
              <div className="assinatura">Assinatura de quem envia (texto e imagem) · link de descadastro</div>
            </PreviaEmailHtml>
          )}
          {[...pa.faltando, ...pc.faltando].length > 0 && <span className="small" style={{ color: 'var(--amber-text)' }}>Sem valor para este cliente: {[...new Set([...pa.faltando, ...pc.faltando])].join(', ')} (o trecho fica em branco; use {'{{variavel|texto padrão}}'}).</span>}
        </div>
      )}
      <span className="small faint">
        O envio é automático, em segundo plano{p?.limiteHora ? `: até ${p.limiteHora} por hora e ${p.limiteDia} por dia por caixa` : ''}. Você acompanha o andamento em Campanhas.
      </span>
      <Erro erro={erro} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={enviando || !p?.elegiveis || p.elegiveis > p.maximo || !assunto.trim() || corpoEmailVazio(corpoLimpo)} onClick={enviar}>
          <Icon name="email" size={15} />{enviando ? 'Enviando…' : `Enviar para ${num(p?.elegiveis ?? 0)} ${p?.elegiveis === 1 ? 'cliente' : 'clientes'}`}
        </button>
      </div>
    </Modal>
  );
}
